/**
 * 業務フローの図（マスター 1 面）のビュー — 枠に見出し・図・詳細を差し込む。
 *
 * **枠は共有する**（`flow-workspace/`）。図の器・詳細の置き場所・全画面・器の実測は枠が持ち、
 * この画面が渡すのは「何を描くか」だけである。ステージの図も主フローをこの部品で描く
 * （`showHeading={false}` で見出しを外す）。
 *
 * **入った直後は図だけにする**（Dhik 2026-09-29「テキスト量が多いことが課題」）。枠は静かにし
 * （1 面の見出しを出さず、全画面・拡大・縮小は図に乗った時だけ出す）、操作の案内と線の描き方の
 * 凡例は図の枠の ⓘ に束ねる。右パネルは業務の箱を押すまで開かず、✕ ・ Esc ・ 同じ箱をもう一度押すと閉じる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー 1 本（節点 5 種・宛先 4 種・入口と出口）を、共通の静かな枠に見出し・図・業務を押したときの右パネルを差し込んで描く。
 * @graph-connects FlowWorkspace [calls] 図・詳細・全画面の枠
 * @graph-connects computeBusinessFlowLayout [calls] レーン帯と節点の座標の計算
 * @graph-connects buildBusinessFlowDiagram [calls] 図 1 面ぶんの中身
 */
import type { FlowView } from '@cortex/domain-spec-console';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '../../components/spec/fallback-states.js';
import { FlowCanvas } from '../flow-workspace/flow-canvas.js';
import { NO_MARKED_TRANSITIONS, type FlowChangeState } from '../flow-workspace/flow-diff-marks.js';
import { FlowWorkspace } from '../flow-workspace/flow-workspace.js';
import { buildBusinessFlowDiagram } from '../business-flow-diagram/diagram.js';
import { computeBusinessFlowLayout, initialViewportOf } from '../business-flow-diagram/layout.js';
import { FlowHeading } from './flow-heading.js';
import { FlowInfoMark, flowFrameInfoLines } from './flow-info-mark.js';
import { opensFlowDetail, StoryFlowDetail } from './story-flow-detail.js';

/** 差分の印が 1 つも無い面。 @graph-connects none */
const NO_NODE_STATES: ReadonlyMap<number, FlowChangeState> = new Map();

/** `StoryFlowView` の props。 @graph-connects none */
export interface StoryFlowViewProps {
  /** 読み終えた業務フロー */
  flow: FlowView;
  /** 図の中で開いている節点の局所 ID */
  selectedNodeId: number | null;
  /**
   * 業務の節点を押したときに伝える。受け口は、選んでいる節点をもう一度受け取ったら選択を外す
   * （右パネルの ✕ ・ Esc もこの口で閉じる）
   */
  onSelect: (nodeId: number) => void;
  /** 見出しを出すか。ステージの図に埋めるときは外す */
  showHeading?: boolean;
  /** 重ねる案件番号。リンクに引き継ぐ。master なら undefined */
  pjNumber?: number;
}

/**
 * 業務フローを、見出し・図・詳細に配って描く。
 *
 * @param props 業務フロー・選択
 * @returns 見出しと枠に収めた業務フロー 1 本
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローを見出し・レーン帯の図・業務を押したときの右パネルに配って描く。
 * @graph-connects computeBusinessFlowLayout [calls] レーン帯と節点の座標の計算
 * @graph-connects FlowWorkspace [calls] 図・詳細・全画面の枠
 * @graph-connects FlowInfoMark [calls] 図の枠の ⓘ
 * @graph-connects flowFrameInfoLines [calls] 図の枠の ⓘ に束ねる操作の案内と凡例
 * @graph-connects opensFlowDetail [calls] 右パネルを開く節点（業務）かの判定
 * @graph-connects StoryFlowDetail [calls] 業務を選んだときの右パネル
 */
export function StoryFlowView({
  flow,
  selectedNodeId,
  onSelect,
  showHeading = true,
  pjNumber,
}: StoryFlowViewProps) {
  const { t } = useTranslation();
  /*
   * 配置は骨格が変わったときだけ計算する。節点を選ぶたびに計算し直すと、結果が同じでも参照が
   * 変わるため図が節点を丸ごと組み直し、選んだ瞬間に図がちらつく。
   */
  const layout = useMemo(() => computeBusinessFlowLayout(flow), [flow]);
  const selected = flow.nodes.find((node) => node.nodeId === selectedNodeId) ?? null;

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      {showHeading && <FlowHeading flow={flow} pjNumber={pjNumber} />}

      <FlowWorkspace
        quiet
        info={<FlowInfoMark lines={flowFrameInfoLines(t, false)} />}
        layoutHeight={layout.height}
        canvasWidth={layout.width}
        placeholder={
          flow.nodes.length === 0 ? <EmptyState message={t('storyFlow.empty')} /> : undefined
        }
        panes={({ paneWidth, scale, height }) => {
          const diagram = buildBusinessFlowDiagram({
            flow,
            layout,
            laneWidth: layout.width,
            stateByNodeId: NO_NODE_STATES,
            markedTransitionIds: NO_MARKED_TRANSITIONS,
            markedKind: 'none',
            selectedNodeId,
            onSelect,
            t,
          });
          return (
            <FlowCanvas
              side="master"
              title={t('storyFlow.diagramTitle')}
              height={height}
              nodes={diagram.nodes}
              edges={diagram.edges}
              laneLabels={diagram.laneLabels}
              /*
               * 初期表示の縮尺は枠が決め（図が枠に入る倍率、下限 0.75）、図の面が当てる。視点は
               * 実寸で作り、寄せ先の計算には面の幅を実寸へ割り戻した値を渡す。
               */
              viewportControl={{
                kind: 'own',
                initial: initialViewportOf(layout.nodes, paneWidth / scale, selectedNodeId),
              }}
            />
          );
        }}
        {...(opensFlowDetail(selected)
          ? {
              detail: (
                <StoryFlowDetail
                  selected={selected}
                  {...(pjNumber === undefined ? {} : { pjNumber })}
                  /*
                   * 選んだ節点をもう一度伝えると外れる（受け口が同じ節点の選択を外す）。✕ ・ Esc も
                   * 同じ口で閉じ、閉じ方を節点をもう一度押すのと揃える。
                   */
                  onClose={() => {
                    onSelect(selected.nodeId);
                  }}
                />
              ),
            }
          : {})}
      />
    </div>
  );
}
